import { r as registerInstance, c as createEvent, h, H as Host, g as getElement } from './index-8f7fb048.js';
import { g as getControlInputAriaLabel } from './aria-f37957bf.js';

const marketToggleCss = ":host,::slotted(*),*{box-sizing:border-box}:host([disabled]){cursor:not-allowed !important;}:host([disabled]) *,:host([disabled]) ::slotted(*){cursor:not-allowed !important;pointer-events:none !important;}:host{position:relative;outline:none;display:inline-block;height:var(--toggle-height)}:host::after{--focus-ring-position:calc(-1 * (var(--toggle-border-size) + var(--toggle-focus-ring-buffer-size) + var(--toggle-focus-ring-border-size)));content:'';position:absolute;top:var(--focus-ring-position);right:var(--focus-ring-position);bottom:var(--focus-ring-position);left:var(--focus-ring-position);display:block;border:var(--toggle-focus-ring-border-size) solid var(--toggle-focus-ring-color);border-radius:calc(var(--toggle-border-radius) + var(--toggle-focus-ring-buffer-size) * 2);opacity:0;pointer-events:none;transition:opacity 0.2s}:host(.focus-visible)::after{opacity:0.5}input{position:absolute;top:0;left:0;width:100%;height:100%;margin:0;outline:none;cursor:pointer;-webkit-appearance:none;-moz-appearance:none;appearance:none}:host svg circle,:host svg rect{transition:var(--row-slotted-control-transition, cx 0.3s)}:host svg circle{fill:var(--toggle-normal-state-unselected-value-thumb-color)}:host svg rect{fill:var(--toggle-normal-state-unselected-value-track-color);stroke:var(--toggle-normal-state-unselected-value-border-color)}:host([checked]) svg circle{fill:var(--toggle-normal-state-selected-value-thumb-color);cx:28px}:host([checked]) svg rect{fill:var(--toggle-normal-state-selected-value-track-color);stroke:var(--toggle-normal-state-selected-value-border-color)}:host(:hover) circle,:host([hovered]) circle{fill:var(--toggle-hover-state-unselected-value-thumb-color)}:host(:hover) rect,:host([hovered]) rect{fill:var(--toggle-hover-state-unselected-value-track-color);stroke:var(--toggle-hover-state-unselected-value-border-color)}:host([checked]:hover) circle,:host([checked][hovered]) circle{fill:var(--toggle-hover-state-selected-value-thumb-color)}:host([checked]:hover) rect,:host([checked][hovered]) rect{fill:var(--toggle-hover-state-selected-value-track-color);stroke:var(--toggle-hover-state-selected-value-border-color)}:host(.focus-visible) circle{fill:var(--toggle-focus-state-unselected-value-thumb-color)}:host(.focus-visible) rect{fill:var(--toggle-focus-state-unselected-value-track-color);stroke:var(--toggle-focus-state-unselected-value-border-color)}:host([checked].focus-visible) circle{fill:var(--toggle-focus-state-selected-value-thumb-color)}:host([checked].focus-visible) rect{fill:var(--toggle-focus-state-selected-value-track-color);stroke:var(--toggle-focus-state-selected-value-border-color)}:host(:active) circle,:host([active]) circle{fill:var(--toggle-pressed-state-unselected-value-thumb-color)}:host(:active) rect,:host([active]) rect{fill:var(--toggle-pressed-state-unselected-value-track-color);stroke:var(--toggle-pressed-state-unselected-value-border-color)}:host([checked]:active) circle,:host([checked][active]) circle{fill:var(--toggle-pressed-state-selected-value-thumb-color)}:host([checked]:active) rect,:host([checked][active]) rect{fill:var(--toggle-pressed-state-selected-value-track-color);stroke:var(--toggle-pressed-state-selected-value-border-color)}:host([disabled]) input{cursor:not-allowed}:host([disabled]) circle{fill:var(--toggle-disabled-state-unselected-value-thumb-color)}:host([disabled]) rect{fill:var(--toggle-disabled-state-unselected-value-track-color);stroke:var(--toggle-disabled-state-unselected-value-border-color)}:host([disabled][checked]) circle{fill:var(--toggle-disabled-state-selected-value-thumb-color)}:host([disabled][checked]) rect{fill:var(--toggle-disabled-state-selected-value-track-color);stroke:var(--toggle-disabled-state-selected-value-border-color)}";

var ARIA_VALUE;
(function (ARIA_VALUE) {
  ARIA_VALUE["TRUE"] = "true";
  ARIA_VALUE["FALSE"] = "false";
})(ARIA_VALUE || (ARIA_VALUE = {}));
const MarketToggle = class {
  constructor(hostRef) {
    registerInstance(this, hostRef);
    this.marketToggleChange = createEvent(this, "marketToggleChange", 7);
    this.checked = false;
    this.disabled = false;
    this.focused = false;
    this.hovered = false;
    this.active = false;
  }
  /**
   * Toggles `checked` prop, and emits a change event accordingly.
   * Used by `market-row` to sync its selected state w/ slotted toggles.
   */
  setSelection(newValue) {
    if (typeof newValue !== 'boolean') {
      return Promise.resolve();
    }
    const prevValue = this.checked;
    if (prevValue !== newValue) {
      const { defaultPrevented } = this.marketToggleChange.emit({
        current: newValue,
        previous: prevValue,
      });
      if (!defaultPrevented) {
        this.checked = newValue;
      }
    }
    return Promise.resolve();
  }
  /**
   * DEPRECATED (3.x): Toggles `selected` state (unrelated to the HTML attribute `value`).
   */
  setValue(newValue) {
    /* eslint-disable-next-line no-console */
    console.warn("market-toggle's setValue() method has been deprecated. Use setSelection() instead.", this.el);
    this.setSelection(newValue);
    return Promise.resolve();
  }
  /**
   * Sets `active` state. Allows external elements to programmatically
   * trigger active styling, ex. when slotted as a control into `market-row`.
   */
  setActive(value) {
    this.active = value;
    return Promise.resolve();
  }
  /**
   * Sets `hovered` state. Allows external elements to programmatically
   * trigger hover styling, ex. when slotted as a control into `market-row`.
   */
  setHover(value) {
    this.hovered = value;
    return Promise.resolve();
  }
  /**
   * Sets `disabled` state. Allows external elements to programmatically
   * trigger disabled styling, ex. when slotted as a control into `market-row`.
   */
  setDisabled(value) {
    this.disabled = value;
    return Promise.resolve();
  }
  handleClick() {
    if (this.disabled) {
      return;
    }
    this.setSelection(!this.checked);
  }
  render() {
    const ariaChecked = this.checked ? ARIA_VALUE.TRUE : ARIA_VALUE.FALSE;
    return (h(Host, { class: "market-toggle", role: "switch", "aria-checked": ariaChecked, onClick: () => {
        this.handleClick();
      } }, h("input", { type: "checkbox", role: "switch", "aria-checked": ariaChecked, "aria-label": getControlInputAriaLabel(this.el), checked: this.checked, disabled: this.disabled }), h("svg", { width: "40", height: "24", viewBox: "0 0 40 24", fill: "none", xmlns: "http://www.w3.org/2000/svg" }, h("rect", { x: "1", y: "1", width: "38", height: "22", rx: "11", "stroke-width": "2" }), h("circle", { cx: "12", cy: "12", r: "7" }))));
  }
  get el() { return getElement(this); }
};
MarketToggle.style = marketToggleCss;

export { MarketToggle as market_toggle };
